import React from "react";
import classNames from "classnames";
import { Formik, Form } from "formik";
import {
  Button,
  Flex,
  TwoFAInput,
  LoadingIndicator,
  HelpIndicator,
  TWO_FA_CODE_LENGTH as CODE_LENGTH,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { FormCheckbox } from "@jsq/ds/forms";
import { type Handle2FAInputs } from "apps/portal/Portal/common/AccountActivation/VerifyTwoFA";
import { useFeatureFlag } from "@jsq/flags-react";
import { type TwoFaType } from "common/types/mainTypes";
import { TwoFaType as TwoFaTypeEnum } from "common/types/mainEnums";
import styles from "./styles.scss";
import { type UnauthenticatedArenaObjectType } from "../../types";
import { CountdownTimer } from "./CountdownTimer";
import { useCountdown } from "../../hooks";

export type FormValue = {
  verificationToken: string;
  rememberTwoFa: boolean;
};

export type VerifyTwoFAFormProps = {
  twoFAInputErrorMessage: string;
  shouldClearTwoFAInput: boolean;
  showCheckbox: boolean;
  showGoBackButton?: boolean;
  isButtonLoading: boolean;
  handleSubmit: (values: FormValue, resetForm: () => void) => void;
  onBackButtonClick?: () => void;
  handleInputChange: (inputs: Handle2FAInputs) => void;
  sendCode: (isResend: boolean) => void;
  arena?: UnauthenticatedArenaObjectType;
  twoFaType?: TwoFaType;
};
export const COUNTDOWN_DURATION = 5 * 60; // 5 minutes
export const EXTENDED_COUNTDOWN_DURATION = 15 * 60; // 15 minutes

export const VerifyTwoFAForm = (props: VerifyTwoFAFormProps) => {
  const {
    twoFAInputErrorMessage,
    shouldClearTwoFAInput,
    showCheckbox,
    showGoBackButton = false,
    onBackButtonClick,
    isButtonLoading,
    handleSubmit,
    handleInputChange,
    sendCode,
    twoFaType,
  } = props;

  const extendSms2faCountdown = useFeatureFlag("extendSms2faValidityWindow", false);
  const countdown = useCountdown(
    extendSms2faCountdown ? EXTENDED_COUNTDOWN_DURATION : COUNTDOWN_DURATION,
  );
  const isTotp = twoFaType === TwoFaTypeEnum.Totp;

  return (
    <Formik
      initialValues={{ verificationToken: "", rememberTwoFa: false }}
      onSubmit={(values, actions) => handleSubmit(values, actions.resetForm)}
    >
      {({ isSubmitting, setFieldValue, submitForm }) => (
        <div className={styles.VerifyTwoFAForm__twoFAInputFlex}>
          <Form>
            <div className={isSubmitting || isButtonLoading ? "stacked-xl" : "stacked-sm"}>
              <TwoFAInput
                size="medium"
                disabled={isSubmitting || isButtonLoading || (!isTotp && countdown.isExpired)}
                onSetValue={(code) => {
                  handleInputChange({
                    code,
                    setFieldValue,
                    submitForm,
                    codeLength: CODE_LENGTH,
                  });
                }}
                errorMessage={twoFAInputErrorMessage}
                shouldClearValue={shouldClearTwoFAInput}
                data-cy="login-2fa"
                data-testid="login-2fa"
              />
            </div>
            {isSubmitting || isButtonLoading ? (
              <div className="stacked-lg">
                <LoadingIndicator size="large" data-testid="loading-indicator" />
              </div>
            ) : (
              <Flex flexDirection="column" alignItems="center">
                <Flex flexDirection="row" justifyContent="center">
                  {isTotp ? null : (
                    <>
                      <CountdownTimer
                        countdownSeconds={countdown.timeRemaining}
                        className="inline-xs"
                      />
                      <p
                        className={classNames(
                          styles.VerifyTwoFAForm__resendText,
                          "text-sm secondary stacked-lg",
                        )}
                        data-cy="two-fa-resend-text"
                      >
                        <button
                          onClick={() => {
                            sendCode(true); // in parent use callback
                            countdown.resetCountdown();
                          }}
                          className={styles.VerifyTwoFAForm__actionLink}
                          type="button"
                          data-cy="send-another-code"
                        >
                          Send another code.
                        </button>
                      </p>
                    </>
                  )}
                </Flex>
                {showCheckbox && (
                  <Flex className="stacked-xl" alignItems="flex-start">
                    <FormCheckbox
                      name="rememberTwoFa"
                      label="Remember this device"
                      wrapperClassName="inline-xs"
                      data-cy="remember-me-checkbox"
                    />
                    <div>
                      <HelpIndicator
                        placement="top"
                        content="You will not be asked for two-factor authentication on this device and
                    browser for the next 30 days"
                      >
                        <Icon type="question-circle" size="medium" variant="outline" />
                      </HelpIndicator>
                    </div>
                  </Flex>
                )}
                {showGoBackButton && onBackButtonClick && (
                  <Button
                    type="button"
                    className="align-center"
                    variant="link"
                    isFullWidth
                    disabled={isSubmitting || isButtonLoading}
                    onClick={onBackButtonClick}
                  >
                    Go back
                  </Button>
                )}
              </Flex>
            )}
          </Form>
        </div>
      )}
    </Formik>
  );
};

export default VerifyTwoFAForm;
